import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useConfigService } from '@thd-nucleus/experience-context';
import { Link } from '@one-thd/sui-atomic-components';
import {
  getApplianceCachedDeliveryDate,
  getCheckAvailabilityText,
  shouldShowApplianceDeliveryFallback
} from '../components/helper/utils';

const CheckEarliestDateCta = ({ enablePLPFasterDeliveryOption, isRetrying, onRetry }) => {
  if (isRetrying) {
    return (
      <div className="sui-flex sui-items-center" data-component="ApplianceDeliveryRetryLoading">
        {/* segment-spinner: This needs to be updated with the MA stencil changes */}
        <span className="!sui-w-9 !sui-h-9 segment-spinner" />
      </div>
    );
  }

  const onKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onRetry(event);
    }
  };

  return (
    // eslint-disable-next-line jsx-a11y/anchor-is-valid
    <Link
      data-component="checkAvailabilityButton"
      data-testid="check-earliest-date-link"
      underline="always"
      role="button"
      tabIndex={0}
      onClick={onRetry}
      onKeyDown={onKeyDown}
    >
      <span className="sui-text-sm">{getCheckAvailabilityText({ enablePLPFasterDeliveryOption })}</span>
    </Link>
  );
};

CheckEarliestDateCta.propTypes = {
  enablePLPFasterDeliveryOption: PropTypes.bool.isRequired,
  isRetrying: PropTypes.bool.isRequired,
  onRetry: PropTypes.func.isRequired
};

/**
 * Owns the major-appliance earliest-delivery-date fallback for the PIP delivery tile: the cached
 * product-query date that stands in for a missing FFA date, and the "Check Earliest Date" CTA
 * shown when neither source has a date.
 *
 * Both tiers are gated to the PIP experience - cart tiles, direct-data (cart/mocked) tiles and
 * bundles keep their existing blank/Unavailable behavior.
 */
export const useApplianceEarliestDateFallback = ({
  applianceDeliveryData,
  applianceDirectData,
  fulfillment,
  isCartTiles,
  isMajorAppliance,
  refetchApplianceDeliveryData
}) => {
  const enablePLPFasterDeliveryOption = !!useConfigService('fs:enablePLPFasterDeliveryOption');
  const [isRetryingApplianceDelivery, setIsRetryingApplianceDelivery] = useState(false);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const isFallbackEligible = isMajorAppliance
    && !isCartTiles
    && !applianceDirectData
    && !fulfillment?.bundleComponents;

  const applianceCachedDeliveryDate = isFallbackEligible
    ? getApplianceCachedDeliveryDate(fulfillment)
    : null;

  const showApplianceDeliveryFallback = isFallbackEligible
    && !applianceCachedDeliveryDate
    && shouldShowApplianceDeliveryFallback(applianceDeliveryData);

  const stopRetryLoader = () => {
    if (isMounted.current) {
      setIsRetryingApplianceDelivery(false);
    }
  };

  const retryApplianceDeliveryDate = (event) => {
    event.stopPropagation();
    if (!refetchApplianceDeliveryData || isRetryingApplianceDelivery) {
      return;
    }
    setIsRetryingApplianceDelivery(true);
    refetchApplianceDeliveryData()
      // eslint-disable-next-line no-console
      .catch((err) => console.error(err))
      .finally(stopRetryLoader);
  };

  const checkEarliestDateCta = showApplianceDeliveryFallback
    ? (
      <CheckEarliestDateCta
        enablePLPFasterDeliveryOption={enablePLPFasterDeliveryOption}
        isRetrying={isRetryingApplianceDelivery}
        onRetry={retryApplianceDeliveryDate}
      />
    )
    : null;

  return {
    applianceCachedDeliveryDate,
    checkEarliestDateCta,
    isRetryingApplianceDelivery,
    showApplianceDeliveryFallback
  };
};
